<template>
  <v-container class="minHeight"
               :class="{'xs': $vuetify.breakpoint.xs}">
    <v-card>
      <full-calendar ref="calendar"
                     :event-sources="eventSources"
                     @event-selected="eventSelected"
                     @day-click="dayClick"
                     :config="config">
      </full-calendar>
      <v-card-actions>
        <v-layout>
          <v-flex text-xs-center>
            <v-tooltip bottom>
              <v-chip small
                      disabled
                      outline
                      color="bookingRemoved"
                      slot="activator">{{ $t('status.BOOKING_REMOVED') }}</v-chip>
              <span>{{ $t('status.tooltip.BOOKING_REMOVED') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <v-chip small
                      disabled
                      outline
                      color="bookingPaid"
                      slot="activator">{{ $t('status.BOOKING_PAID') }}</v-chip>
              <span>{{ $t('status.tooltip.BOOKING_PAID') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <v-chip small
                      disabled
                      outline
                      color="bookingAccepted"
                      slot="activator">{{ $t('status.BOOKING_ACCEPTED') }}</v-chip>
              <span>{{ $t('status.tooltip.BOOKING_ACCEPTED') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <v-chip small
                      disabled
                      outline
                      color="bookingInUse"
                      slot="activator">{{ $t('status.BOOKING_IN_USE') }}</v-chip>
              <span>{{ $t('status.tooltip.BOOKING_IN_USE') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <v-chip small
                      disabled
                      outline
                      color="bookingReubicationCancelled"
                      slot="activator">{{ $t('status.BOOKING_REUBICATION_CANCELLED') }}</v-chip>
              <span>{{ $t('status.tooltip.BOOKING_REUBICATION_CANCELLED') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <v-chip small
                      disabled
                      outline
                      color="bookingSubscription"
                      slot="activator">{{ $t('status.BOOKING_SUBSCRIPTION') }}</v-chip>
              <span>{{ $t('status.tooltip.BOOKING_SUBSCRIPTION') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <v-chip small
                      disabled
                      outline
                      color="bookingSystem"
                      slot="activator">{{ $t('status.BOOKING_SYSTEM') }}</v-chip>
              <span>{{ $t('status.tooltip.BOOKING_SYSTEM') }}</span>
            </v-tooltip>

          </v-flex>
        </v-layout>
      </v-card-actions>
    </v-card>

    <reservation-detail v-if="showDetail"
                        :bookingSelected="bookingSelected.id"
                        @on-bottom-sheet-closed="bottomSheetClosed">
    </reservation-detail>
  </v-container>
</template>

<script>
import { FullCalendar } from 'vue-full-calendar';
import 'fullcalendar/dist/locale/es';
import reservationService from '@/services/reservationService';
import manageErrorService from '@/services/manageErrorService';
import ReservationDetail from '@/components/commons/ReservationDetail';
import moment from 'moment';
import { EventBus } from '@/services/eventBus';
import keys from '@/store/keys';
import store from '@/store';
import _ from 'lodash';
import 'fullcalendar/dist/fullcalendar.min.css';

export default {
  name: 'ReservationCalendar',
  components: {
    FullCalendar,
    [ReservationDetail.name]: ReservationDetail
  },
  watch: {
    showDetail: function(val) {
      document.querySelector('html').style.overflow = val ? 'hidden' : null;
    }
  },
  data() {
    const self = this;
    return {
      showDetail: false,
      dataUserId: self.userId,
      eventSources: [
        {
          async events(start, end, timezone, callback) {
            let params = {
              userId: self.dataUserId,
              start: start.utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
              end: end.utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]')
            };
            let results = await reservationService.list(params);
            if (results.error) {
              manageErrorService.handle(results.data);
              return;
            }
            let events = results.data;

            let calendarEvents = _.map(events, evt => {
              let end = null;
              let type = evt.type;
              if (evt.status === 'PAID' || evt.type === 'SUBSCRIPTION') {
                end = moment(evt.endLocal).utc();
              } else if (evt.hasMaxAvailability) {
                if (self.showMaxAvailability(evt.endLocal, evt.maxAvailability)) {
                  end = moment(evt.maxAvailability).utc();
                }
              }
              if (type !== 'SUBSCRIPTION') {
                type = 'BOOKING';
              }

              return {
                start: moment(evt.startLocal).utc(),
                end: end,
                title: `${evt.parking.name}`,
                allDay: false,
                reservationId: evt.id,
                parking: evt.parking,
                className: self.getEventClassColor(evt),
                type: _.toLower(type)
              };
            });
            callback(calendarEvents);
          }
        }
      ],
      config: {
        firstDay: store.state.langSelected.lang === 'en' ? 0 : 1,
        defaultView: 'month',
        allDaySlot: false,
        contentHeight: (window.innerHeigh || document.documentElement.clientHeight || document.body.clientHeight) - 270,
        displayEventEnd: true,
        nextDayThreshold: '00:00:00',
        editable: false,
        locale: store.state.langSelected.lang,
        header: {
          left: 'prev,today,next',
          center: 'title',
          right: 'month,agendaWeek,listWeek'
        },
        buttonText: {
          today: this.$i18n.t('reservation.calendar.button.today'),
          month: this.$i18n.t('reservation.calendar.button.month'),
          week: this.$i18n.t('reservation.calendar.button.week'),
          day: this.$i18n.t('reservation.calendar.button.day'),
          list: this.$i18n.t('reservation.calendar.button.list')
        },
        // ,agendaDay
        eventRender: function(event, element) {}
      },
      bookingSelected: {},
      day: null
    };
  },
  created() {
    EventBus.$on(keys.eventOnLanguageChanged, this.translate);
    EventBus.$on(keys.closeReservationDetailAndRefreshCalendar, this.doCloseReservationDetailAndRefreshCalendar);
  },
  destroyed() {
    EventBus.$off(keys.eventOnLanguageChanged, this.translate);
    EventBus.$off(keys.closeReservationDetailAndRefreshCalendar, this.doCloseReservationDetailAndRefreshCalendar);
  },
  methods: {
    bottomSheetClosed() {
      this.showDetail = false;
      this.bookingSelected = {};
      this.$refs.calendar.fireMethod('refetchEvents');
    },
    translate() {
      this.config.buttonText.today = this.$i18n.t('reservation.calendar.button.today');
      this.config.buttonText.month = this.$i18n.t('reservation.calendar.button.month');
      this.config.buttonText.week = this.$i18n.t('reservation.calendar.button.week');
      this.config.buttonText.day = this.$i18n.t('reservation.calendar.button.day');
      this.config.buttonText.list = this.$i18n.t('reservation.calendar.button.list');
      this.config.locale = store.state.langSelected.langIso.substr(0, 2);

      if (typeof this.$refs.calendar !== 'undefined') {
        this.$refs.calendar.fireMethod('option', 'locale', this.config.locale);
      }
    },
    eventSelected(event, jsEvent, view) {
      if (event.className[0] === 'bookingRemoved') {
        return;
      }
      this.showDetail = true;
      this.bookingSelected = {
        id: event.reservationId
      };
    },
    dayClick(date, jsEvent, view) {},
    getEventClassColor(reservation) {
      let eventClass = 'label-default';
      if (reservation.type === 'SUBSCRIPTION' && reservation.status !== 'PRE_SUBSCRIPTION') {
        eventClass = 'bookingSubscription';
      } else if (reservation.status === 'ACCEPTED') {
        eventClass = ['bookingAccepted', 'bookingAccepted--text'];
      } else if (reservation.status === 'IN_USE') {
        eventClass = 'bookingInUse';
      } else if (reservation.status === 'REMOVED') {
        eventClass = 'bookingRemoved';
      } else if (reservation.status === 'CANCELLED') {
        eventClass = 'bookingReubicationCancelled';
      } else if (reservation.status === 'PAID') {
        eventClass = ['bookingPaid', 'bookingPaid--text'];
      } else if (reservation.type === 'SYSTEM') {
        eventClass = 'bookingSystem';
      }
      return eventClass;
    },
    doCloseReservationDetailAndRefreshCalendar(params) {
      this.bottomSheetClosed();
      this.$refs.calendar.fireMethod('refetchEvents');
    },
    showMaxAvailability(endLocal, maxAvailability) {
      return moment(endLocal)
        .utc()
        .add(1, 'day')
        .isAfter(maxAvailability);
    }
  }
};
</script>

<style lang="scss">
#calendar .fc-header-toolbar {
  padding: 20px 20px 10px 20px;
  background-color: #88c1df;
}

#calendar .fc-header-toolbar .fc-center {
  color: white;
}

#calendar .fc-header-toolbar h2,
#calendar .fc-list-item,
#calendar .fc-day-header {
  font-weight: 500;
}

#calendar .fc-list-item:hover td {
  background-color: initial !important;
  cursor: pointer;
}

#calendar .fc-prev-button,
#calendar .fc-next-button {
  min-width: 60px !important;
}

#calendar .fc-time-grid .fc-content,
#calendar .fc-event-container .fc-content {
  color: white !important;
}

#calendar .fc-time-grid .fc-content .fc-time {
  font-weight: 600;
}
#calendar .fc-time-grid .fc-content .fc-title {
  font-weight: 500;
  margin-top: 3px;
}

#calendar .fc-list-table .fc-list-item {
  background-color: white !important;
}
#calendar .fc-list-item-marker {
  display: none;
}
#calendar .fc-list-heading-alt,
#calendar .fc-list-heading-main {
  color: #616161 !important;
  caret-color: #616161 !important;
}

.xs #calendar .fc-button-group {
  display: inline-grid;
}

.fc-button {
  text-transform: initial;
  border: 1px solid currentColor;
  background: white !important;
  -webkit-box-shadow: none;
  box-shadow: none;
  -webkit-appearance: button;
  color: #5ea4c8 !important;
  caret-color: #5ea4c8 !important;
  border-radius: 28px;
  padding: 0 16px;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  border-radius: 2px;
  display: -webkit-inline-box;
  display: -ms-inline-flexbox;
  display: inline-flex;
  height: 36px;
  -webkit-box-flex: 0;
  -ms-flex: 0 0 auto;
  flex: 0 0 auto;
  font-size: 14px;
  font-weight: 500;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  margin: 6px 8px;
  min-width: 88px;
  outline: 0;
  text-decoration: none;
  -webkit-transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1), color 1ms;
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1), color 1ms;
  position: relative;
  vertical-align: middle;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
</style>
